import { useLocation } from 'react-router-dom';
import { useQuery, useMutation } from '@tanstack/react-query';
import { useMemo } from 'react';
import { QUERY_KEYS } from '@constants/queryKeys';

import {
  fetchLatestWhatsNew,
  fetchUserWhatsNew,
  ICreateUserWhatsNew,
  createUserWhatsNew,
} from '@auth_module/services';
import { useUser } from '@auth_module/hooks/useUser';

export type WhatsNewType = {
  id: number;
  version: string;
  product: string | null;
  option: string;
  title: string;
  description: string;
  image_url: string;
  publish_date: string;
};

export type UserWhatsNew = {
  created_at: string | null;
  id: number;
  read_at: string | null;
  updated_at: string | null;
  user_id: string;
  whats_new: WhatsNewType;
  whats_new_id: number;
};

const fetchLatestWhatsNewQueryFn = async () => {
  const { data } = await fetchLatestWhatsNew();
  return data as WhatsNewType[];
};

const fetchUserWhatsNewQueryFn = async (str: string) => {
  const { data } = await fetchUserWhatsNew(str);
  return data as UserWhatsNew[];
};

export function useWhatsNew() {
  const { pathname } = useLocation();
  const { userFeatures } = useUser();

  const {
    data: latest,
    error: latestError,
    isLoading: isLatestNewLoading,
  } = useQuery({
    queryKey: [QUERY_KEYS.WHATS_NEW_LATEST],
    queryFn: fetchLatestWhatsNewQueryFn,
  });

  const {
    data: userWhatsNew,
    error: userWhatsNewError,
    isLoading: isUserWhatsNewLoading,
    refetch: refetchUserWhatsNew,
  } = useQuery({
    queryKey: [QUERY_KEYS.USER_WHATS_NEW, latest?.[0]?.version || ''],
    queryFn: () => fetchUserWhatsNewQueryFn(latest?.[0]?.version || ''),
    enabled: !!latest?.length,
  });

  const { mutate: mutateUserWhatsNew, isLoading: isMutateUserWhatsNewLoading } = useMutation({
    mutationFn: (payload: ICreateUserWhatsNew) => createUserWhatsNew(payload),
    onSettled: () => {
      refetchUserWhatsNew();
    },
  });

  // latest whats new is determined by user features and product user currently using (which we determine from url path), or if no user features is related to the latest whats new version then we use the default
  const userLatestWhatsNew = useMemo(() => {
    if (!latest?.length) return undefined;

    const features = userFeatures?.map((feature) => feature.feature_name);
    const latestByUserFeatures = latest?.find(
      (item) =>
        features?.includes(item.option) &&
        (pathname.split('/').includes(item?.product || '') || item.product === 'any')
    );
    const latestDefaultOption = latest?.find(
      (item) =>
        item.option === 'default' &&
        (pathname.split('/').includes(item?.product || '') || item.product === 'any')
    );

    return latestByUserFeatures || latestDefaultOption;
  }, [latest, userFeatures]);

  return {
    userWhatsNew,
    userWhatsNewError,
    isUserWhatsNewLoading,
    latest: latest?.length ? latest : [],
    hasSeenLatestVersion: !!userWhatsNew?.length,
    latestError,
    isLatestNewLoading,
    isMutateUserWhatsNewLoading,
    mutateUserWhatsNew,
    userLatestWhatsNew,
  };
}
